import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bookmark } from '../../Bookmark';
import { getCardTime } from '../../../utils/helpers';
import ItemCarousel from '../../ItemCarousel';
import Checkbox from '../../Bulk/Checkbox';
import { EllipsisText, FlexBetween, FlexibleDiv } from '../../../styled/common';
import {
  BillOfSaleIcon,
  CardFooter,
  CardLink,
  CardParams,
  CardParamsLocation,
  CardParamsName,
  CardPreview,
  CardPreviewImage,
  FeaturedIcon,
  ItemCardStyled,
  Label,
  MortgageIcon,
  PriceContainer,
  VippedIcon,
  BulkItemLabel,
  BulkItemCheckMark
} from './styled';
import { getLocationName, getParamsList, joinTupleValues } from './heplers';
import { ArrowNext, ArrowPrev } from '../Swiper/styled';
import { BaseItemProps, SwiperDirection } from './types';
import { PriceDisplay } from './PriceDisplay';
import { profileItemStateLabels } from '../../../utils/helpers/profileItemStatuses';
import { Breakpoints } from '../../../styled/constants';

const ItemCardComponent = <T extends BaseItemProps>(props: T) => {
  const { t, i18n } = useTranslation();

  const {
    stat,
    path,
    photos,
    photosCount,
    price,
    paidDaily,
    leased,
    vipped,
    city,
    hasBillOfSale,
    hasMortgage,
    featured,
    id,
    inBookmarks,
    shouldUpdateCache,
    removeItemHandle,
    carousel = false,
    itemId,
    setCarousel,
    updatedAt,
    cypress = 'item-card-link',
    company,
    isBusiness,
    hideExcessItems = false,
    showBookmark = true,
    hasHorizontalScroll = false,
    bulkItem = false,
    handleClick,
    checkedItems,
    rooms,
    floor,
    area,
    floors,
    location,
    business,
    'cypress-card': card = 'item-card',
    button,
    deskCountItemsPerRows,
    hideLabels,
    priceView,
    state,
    showStateLabel = false,
    forceTargetBlank = false,
    priority = false,
    forceLargeImage = false
  } = props;

  const locationName = getLocationName({ city, location });
  const paramsList = getParamsList({ rooms, area, floor, floors }, t);
  const cardTime = getCardTime(updatedAt, i18n);
  const setCarouselId = () => setCarousel(id);

  const isCarouselLoaded = itemId === id;
  const [swiper, updateSwiper] = useState<SwiperDirection | null>(null);
  const [startX, updateStartX] = useState(null);
  const [startY, updateStartY] = useState(null);

  const other = useMemo(
    () => joinTupleValues((paramsList ?? []) as [string, string][]),
    [paramsList]
  );

  const imgAlt = useMemo(
    () =>
      t('cards.item_card.img_alt', {
        id,
        city: city.name,
        location: locationName,
        other,
        interpolation: { escapeValue: false }
      }),
    [id, city.name, locationName, other]
  );

  const thumbnail = photos.map((sub, subindex) => (
    <picture key={subindex}>
      <source
        media={`(max-width: ${Breakpoints.tablet}px)`}
        srcSet={forceLargeImage ? sub.f460x345 : sub.thumbnail}
      />
      <CardPreviewImage
        src={sub.f460x345 || sub.thumbnail}
        alt={imgAlt}
        width={460}
        height={345}
        {...(priority ? { fetchpriority: 'high' } : { loading: 'lazy' })}
      />
    </picture>
  ));

  const scrollOnStart = e => {
    const touchobj = e.changedTouches[0];
    updateStartX(touchobj.pageX);
    updateStartY(touchobj.pageY);
  };

  const scrollOnMove = e => {
    const { pageX, pageY } = e.changedTouches[0];
    const distX = pageX - startX;
    const distY = pageY - startY;
    if (Math.abs(distX) - Math.abs(distY) > 45) {
      setCarouselId();
      updateSwiper(distX < 0 ? SwiperDirection.Next : SwiperDirection.Prev);
    }
  };

  const itemType = company?.targetType?.toLowerCase() || '';

  const createTouchHandler = handler => e =>
    !carousel || isCarouselLoaded ? null : handler(e);

  const onTouchStartHandler = createTouchHandler(scrollOnStart);
  const onTouchMoveHandler = createTouchHandler(scrollOnMove);

  const onSwipHandler = (cursor = SwiperDirection.Next) => {
    setCarouselId();
    updateSwiper(cursor);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey) return;

    const isDesktop = window.matchMedia(
      `(min-width: ${Breakpoints.smallDesktop}px)`
    ).matches;

    if (isDesktop || forceTargetBlank) {
      e.preventDefault();
      window.open(path, '_blank');
    }
  };

  const carouselBtns = carousel && !isCarouselLoaded && (
    <>
      <ArrowNext
        $zIndex="0"
        onClick={onSwipHandler}
        onKeyPress={setCarouselId}
      />
      <ArrowPrev
        $zIndex="0"
        onClick={() => onSwipHandler(SwiperDirection.Prev)}
        onKeyPress={setCarouselId}
      />
    </>
  );

  const cardLinkProps = {
    onClick: handleCardClick
  };

  const ifCarousel = isCarouselLoaded ? (
    <ItemCarousel
      {...{ photos, carousel, path, photosCount }}
      scrollTo={swiper}
    />
  ) : (
    <CardLink
      href={path}
      data-cy="card-link"
      aria-label={`Photo ${paramsList}`}
      {...cardLinkProps}
    >
      {thumbnail[0]}
    </CardLink>
  );

  return (
    <ItemCardStyled
      data-cy={card}
      $hideExcessItems={hideExcessItems}
      $hasHorizontalScroll={hasHorizontalScroll}
      $deskCountItemsPerRows={deskCountItemsPerRows}
      className="item-card"
      data-stat={stat}
    >
      <CardLink
        $withCarousel={carousel}
        href={path}
        cypress={cypress}
        $hasButton={!!button}
        aria-label={paramsList.join(',')}
        {...cardLinkProps}
      />
      {showBookmark && (
        <Bookmark
          itemId={id}
          isActive={inBookmarks}
          removeItemFunc={removeItemHandle}
          stat="product-card-bookmark"
          shouldUpdateCache={shouldUpdateCache}
        />
      )}
      <CardPreview
        onTouchStart={onTouchStartHandler}
        onTouchMove={onTouchMoveHandler}
        data-cy="item-card-preview"
        $bulkChecked={bulkItem && checkedItems?.includes(Number(id))}
      >
        {ifCarousel}
        {carouselBtns}
        {!!itemType.length && (isBusiness || business?.id) && !hideLabels && (
          <Label
            tag="caption_2"
            $itemType={itemType}
            data-cy={`product-label-${itemType}`}
          >
            {t(`cards.item_card.${itemType}`)}
          </Label>
        )}
        {state && showStateLabel && (
          <Label
            tag="caption_1_new"
            $itemState={state}
            data-cy={`product-label-${state.toLowerCase()}`}
          >
            {t(profileItemStateLabels[state])}
          </Label>
        )}
      </CardPreview>
      <CardParams>
        <FlexBetween>
          <PriceContainer tag="h5_new" className="price-container">
            <PriceDisplay
              mode={priceView}
              price={{
                full: price?.total,
                currency: price?.currency,
                perAre: price?.perAre,
                units: area?.units
              }}
              leased={leased}
              paidDaily={paidDaily}
            />
          </PriceContainer>
          <FlexibleDiv>
            {hasBillOfSale && <BillOfSaleIcon />}
            {hasMortgage && <MortgageIcon />}
          </FlexibleDiv>
        </FlexBetween>
        <CardParamsLocation tag="body_1">{locationName}</CardParamsLocation>
        <CardParamsName tag="body_2">
          {paramsList.map(([key, value]) => (
            <span key={key}>{value}</span>
          ))}
        </CardParamsName>
        <CardFooter color="secondary" tag="caption_1">
          <FlexBetween>
            <EllipsisText suppressHydrationWarning data-cy="city_when">
              {city.name}, {cardTime}
            </EllipsisText>

            {featured && <FeaturedIcon />}
            {!featured && vipped && <VippedIcon />}
          </FlexBetween>

          {button}
        </CardFooter>
      </CardParams>
      {bulkItem && (
        <BulkItemLabel htmlFor={Number(id)}>
          {t('select')}
          <Checkbox
            key={id}
            type="checkbox"
            id={Number(id)}
            handleClick={handleClick}
            isChecked={checkedItems?.includes(Number(id))}
          />
          <BulkItemCheckMark />
        </BulkItemLabel>
      )}
    </ItemCardStyled>
  );
};

export const ItemCard = memo(ItemCardComponent);
